import React, { useState } from 'react';
import { cn } from '../lib/utils';
import { getUserAvatarUrl, getUserInitials } from '../utils/avatar';

export interface UserAvatarProps {
  name?: string;
  email?: string;
  avatarUrl?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  alt?: string;
  shape?: 'circle' | 'square';
}

const SIZE_CLASSES = {
  xs: 'size-5 text-[8.5px]',
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
  xl: 'size-12 text-base',
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  email,
  avatarUrl,
  size = 'md',
  className,
  alt,
  shape = 'circle',
}) => {
  const [hasError, setHasError] = useState(false);
  const src = getUserAvatarUrl({ name, email, avatar: avatarUrl });
  const initial = getUserInitials(name, email);

  // An original profile from Google SSO or custom upload is not an external placeholder
  const isOriginalProfile = Boolean(
    src &&
    !src.includes('ui-avatars.com') &&
    (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/') || src.startsWith('data:') || src.startsWith('blob:'))
  );

  const roundedClass = shape === 'square' ? 'rounded-lg' : 'rounded-full';

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden font-bold bg-primary/10 text-primary border border-primary/20 select-none shadow-xs',
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        roundedClass,
        className
      )}
      title={name || email || 'User'}
    >
      {!hasError && isOriginalProfile ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className={cn('size-full object-cover', roundedClass)}
          onError={() => setHasError(true)}
          loading="lazy"
        />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
};

